import React, { useState, useRef, useEffect } from 'react';

const ELEMENT_COMPOSITION = [
  { symbol: 'O', name: 'Oxygen', pct: 65.0, color: '#4a9eff', protons: 8, neutrons: 8 },
  { symbol: 'C', name: 'Carbon', pct: 18.5, color: '#888', protons: 6, neutrons: 6 },
  { symbol: 'H', name: 'Hydrogen', pct: 9.5, color: '#50e0a0', protons: 1, neutrons: 0 },
  { symbol: 'N', name: 'Nitrogen', pct: 3.2, color: '#ff9040', protons: 7, neutrons: 7 },
  { symbol: 'Ca', name: 'Calcium', pct: 1.5, color: '#f0c040', protons: 20, neutrons: 20 },
  { symbol: 'P', name: 'Phosphorus', pct: 1.0, color: '#e060ff', protons: 15, neutrons: 16 },
  { symbol: 'Other', name: 'Other elements', pct: 1.3, color: 'rgba(255,255,255,0.3)', protons: 12, neutrons: 12 },
];

const PROTON_MASS_KG = 1.6726e-27;
const NEUTRON_MASS_KG = 1.6749e-27;
const ELECTRON_MASS_KG = 9.109e-31;
const AMU_KG = 1.6605e-27;
const HIGGS_FRACTION_QUARK = 0.01; // ~1% of nucleon mass from Higgs

function formatScientific(n) {
  if (n === 0) return '0';
  const exp = Math.floor(Math.log10(Math.abs(n)));
  const coeff = (n / Math.pow(10, exp)).toFixed(2);
  return `${coeff} × 10^${exp}`;
}

function calculate(weightKg) {
  // Average atomic mass per atom (weighted by body composition)
  // Hydrogen: 1 amu, Oxygen: 16, Carbon: 12, etc.
  const avgAMU = 7.5; // approximate for human body
  const totalAtoms = (weightKg / (avgAMU * AMU_KG));

  // Nucleons
  const avgProtonsPerAtom = 6.0;
  const avgNeutronsPerAtom = 6.2;
  const totalProtons = totalAtoms * avgProtonsPerAtom;
  const totalNeutrons = totalAtoms * avgNeutronsPerAtom;
  const totalElectrons = totalProtons;
  const totalNucleons = totalProtons + totalNeutrons;
  const totalQuarks = totalNucleons * 3;

  // Mass breakdown
  const protonMassTotal = totalProtons * PROTON_MASS_KG;
  const neutronMassTotal = totalNeutrons * NEUTRON_MASS_KG;
  const electronMassTotal = totalElectrons * ELECTRON_MASS_KG;
  const nucleonTotal = protonMassTotal + neutronMassTotal;

  // Higgs vs QCD
  const higgsMass = nucleonTotal * HIGGS_FRACTION_QUARK;
  const qcdMass = nucleonTotal * (1 - HIGGS_FRACTION_QUARK);
  const electronHiggsMass = electronMassTotal; // electrons are 100% Higgs mass

  const totalHiggsMass = higgsMass + electronHiggsMass;
  const totalQCDMass = qcdMass;

  // Hydrogen atoms (single proton, no neutrons) 
  const hydrogenAtoms = totalAtoms * (9.5 / 100);

  return {
    weightKg,
    totalAtoms,
    totalProtons,
    totalNeutrons,
    totalElectrons,
    totalNucleons,
    totalQuarks,
    totalGluons: totalQuarks * 2.5, // approximate virtual gluons
    higgsMassKg: totalHiggsMass,
    qcdMassKg: totalQCDMass,
    electronMassKg: electronMassTotal,
    higgsPct: (totalHiggsMass / weightKg) * 100,
    qcdPct: (totalQCDMass / weightKg) * 100,
    hydrogenAtoms,
  };
}

export default function WhatAmIMadeOf({ mode }) {
  const [weight, setWeight] = useState('');
  const [unit, setUnit] = useState('kg');
  const [result, setResult] = useState(null);
  const [animating, setAnimating] = useState(false);
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const isRuach = mode === 'ruach';
  const accentColor = isRuach ? '#ffb060' : '#f0c040';

  const handleCalculate = () => {
    const w = parseFloat(weight);
    if (!w || w <= 0 || w > 1000) return;
    const kg = unit === 'lbs' ? w * 0.453592 : w;
    setResult(calculate(kg));
    setAnimating(true);
    setTimeout(() => setAnimating(false), 1200);
  };

  useEffect(() => {
    if (!result) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    let t = 0;

    const draw = () => {
      t += 0.015;
      ctx.clearRect(0, 0, W, H);
      const cx = W / 2, cy = H / 2;

      // Animated particle field representing body composition
      ELEMENT_COMPOSITION.forEach((el, ei) => {
        const count = Math.round(el.pct / 5);
        for (let i = 0; i < count; i++) {
          const seed = ei * 100 + i;
          const angle = (seed / (ELEMENT_COMPOSITION.length * 20)) * Math.PI * 2 + t * (0.1 + ei * 0.03);
          const r = 30 + (seed % 8) * 12;
          const px = cx + Math.cos(angle) * r;
          const py = cy + Math.sin(angle) * r;
          const pulse = 0.6 + 0.4 * Math.sin(t * 1.5 + seed);

          ctx.beginPath();
          ctx.arc(px, py, 3 + pulse * 2, 0, Math.PI * 2);
          ctx.fillStyle = el.color + Math.round(pulse * 180).toString(16).padStart(2, '0');
          ctx.fill();
        }
      });

      // Center label
      ctx.font = 'bold 13px Space Mono, monospace';
      ctx.fillStyle = '#fff';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(`${result.weightKg.toFixed(1)} kg`, cx, cy - 6);
      ctx.font = '9px Space Mono, monospace';
      ctx.fillStyle = 'rgba(255,255,255,0.4)';
      ctx.fillText('you', cx, cy + 10);

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [result]);

  return (
    <div>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', letterSpacing: '0.1em', marginBottom: '1.5rem', fontFamily: 'Space Mono', textTransform: 'uppercase' }}>
        {isRuach ? 'How much of you is Ruach-given weight?' : 'Enter your weight — see what you\'re made of'}
      </p>

      {/* Input */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center' }}>
        <input
          type="number" placeholder={isRuach ? 'Your weight...' : 'Enter weight...'}
          value={weight} onChange={e => setWeight(e.target.value)}
          style={{
            flex: 1, padding: '0.75rem 1rem', borderRadius: '8px', fontSize: '1rem',
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
            color: '#fff', fontFamily: 'Space Mono', outline: 'none',
          }}
        />
        <div style={{ display: 'flex', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.15)' }}>
          {['kg', 'lbs'].map(u => (
            <button key={u} onClick={() => setUnit(u)} style={{
              padding: '0.75rem 0.875rem', background: unit === u ? `${accentColor}25` : 'rgba(255,255,255,0.04)',
              color: unit === u ? accentColor : 'rgba(255,255,255,0.4)',
              border: 'none', cursor: 'pointer', fontFamily: 'Space Mono', fontSize: '0.78rem',
            }}>{u}</button>
          ))}
        </div>
        <button onClick={handleCalculate} disabled={!weight} style={{
          padding: '0.75rem 1.25rem', borderRadius: '8px', cursor: weight ? 'pointer' : 'default',
          background: weight ? `${accentColor}25` : 'rgba(255,255,255,0.04)',
          border: `1px solid ${weight ? accentColor + '50' : 'rgba(255,255,255,0.1)'}`,
          color: weight ? accentColor : 'rgba(255,255,255,0.25)',
          fontFamily: 'Space Mono', fontSize: '0.78rem', letterSpacing: '0.05em',
          transition: 'all 0.15s',
        }}>
          {isRuach ? 'Reveal' : 'Calculate'}
        </button>
      </div>

      {result && (
        <>
          {/* Canvas visualization */}
          <canvas ref={canvasRef} width={280} height={220}
            style={{ width: '100%', maxWidth: '280px', display: 'block', margin: '0 auto 1.25rem' }} />

          {/* Key numbers */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '1.25rem' }}>
            {[
              { label: isRuach ? 'Atoms (formed things)' : 'Total atoms', value: formatScientific(result.totalAtoms), color: accentColor },
              { label: isRuach ? 'Quarks (utterances)' : 'Total quarks', value: formatScientific(result.totalQuarks), color: '#4a9eff' },
              { label: isRuach ? 'Protons (uud)' : 'Protons', value: formatScientific(result.totalProtons), color: '#4a9eff' },
              { label: isRuach ? 'Electrons (sparks)' : 'Electrons', value: formatScientific(result.totalElectrons), color: '#50e0a0' },
              { label: isRuach ? 'Neutrons (udd)' : 'Neutrons', value: formatScientific(result.totalNeutrons), color: '#60b0ff' },
              { label: isRuach ? 'Virtual gluons' : 'Virtual gluons (~)', value: formatScientific(result.totalGluons), color: '#e060ff' },
            ].map(item => (
              <div key={item.label} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '0.75rem' }}>
                <div style={{ fontSize: '0.62rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgba(255,255,255,0.3)', marginBottom: '0.25rem' }}>
                  {item.label}
                </div>
                <div style={{ fontSize: '0.85rem', fontFamily: 'Space Mono', color: item.color, fontWeight: 700 }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>

          {/* The big reveal - Higgs vs QCD */}
          <div style={{ borderRadius: '12px', overflow: 'hidden', border: `1px solid ${accentColor}30`, marginBottom: '1.25rem' }}>
            <div style={{ padding: '0.875rem 1rem', background: `${accentColor}0d`, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ fontSize: '0.65rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.1em', color: accentColor, marginBottom: '0.2rem' }}>
                {isRuach ? 'Where your weight comes from' : 'Where your mass comes from'}
              </div>
              <div style={{ fontSize: '1rem', fontWeight: 700, fontFamily: 'Syne', color: '#fff' }}>
                {result.weightKg.toFixed(1)} kg total
              </div>
            </div>

            {/* Higgs mass */}
            <div style={{ padding: '0.875rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.82rem', color: accentColor, fontFamily: 'Syne', fontWeight: 600 }}>
                  {isRuach ? 'Ruach-given weight (Higgs field)' : 'Higgs field mass'}
                </span>
                <span style={{ fontFamily: 'Space Mono', fontSize: '0.8rem', color: accentColor }}>
                  ~{result.higgsPct.toFixed(2)}%
                </span>
              </div>
              <div style={{ height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', marginBottom: '4px' }}>
                <div style={{ width: `${Math.max(0.5, result.higgsPct)}%`, height: '100%', background: accentColor, borderRadius: '3px', opacity: 0.8 }} />
              </div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', fontFamily: 'Space Mono' }}>
                ≈ {(result.higgsMassKg * 1000).toFixed(4)} g — mostly from electrons (100% Higgs) + quark Higgs mass (~1% of nucleons)
              </div>
            </div>

            {/* QCD mass */}
            <div style={{ padding: '0.875rem 1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                <span style={{ fontSize: '0.82rem', color: '#e060ff', fontFamily: 'Syne', fontWeight: 600 }}>
                  {isRuach ? 'Hidden binding energy (QCD/gluons)' : 'QCD binding energy (gluons)'}
                </span>
                <span style={{ fontFamily: 'Space Mono', fontSize: '0.8rem', color: '#e060ff' }}>
                  ~{result.qcdPct.toFixed(1)}%
                </span>
              </div>
              <div style={{ height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', marginBottom: '4px' }}>
                <div style={{ width: `${result.qcdPct}%`, height: '100%', background: '#e060ff', borderRadius: '3px', opacity: 0.8 }} />
              </div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', fontFamily: 'Space Mono' }}>
                ≈ {(result.qcdMassKg).toFixed(2)} kg — kinetic energy of quarks + gluon field energy inside protons & neutrons
              </div>
            </div>
          </div>

          {/* Element breakdown */}
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '0.65rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.25)', marginBottom: '0.75rem' }}>
              {isRuach ? 'Elements — the named forms' : 'Elemental composition'}
            </div>
            {ELEMENT_COMPOSITION.map(el => (
              <div key={el.symbol} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '5px' }}>
                <div style={{ width: '28px', fontSize: '0.75rem', fontFamily: 'Space Mono', color: el.color, textAlign: 'right', flexShrink: 0 }}>{el.symbol}</div>
                <div style={{ flex: 1, height: '5px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px' }}>
                  <div style={{ width: `${el.pct}%`, height: '100%', background: el.color, borderRadius: '3px', opacity: 0.7 }} />
                </div>
                <div style={{ fontSize: '0.7rem', fontFamily: 'Space Mono', color: 'rgba(255,255,255,0.4)', width: '36px', textAlign: 'right' }}>{el.pct}%</div>
              </div>
            ))}
          </div>

          {/* The profound conclusion */}
          <div style={{ padding: '1rem', borderRadius: '10px', border: `1px solid ${accentColor}25`, background: `${accentColor}08` }}>
            <p style={{ fontSize: '0.84rem', color: 'rgba(255,255,255,0.68)', lineHeight: 1.8, margin: 0 }}>
              {isRuach
                ? <>Of your <strong style={{ color: '#fff' }}>{result.weightKg.toFixed(1)} kg</strong>, only about <strong style={{ color: accentColor }}>{(result.higgsMassKg * 1000).toFixed(3)} grams</strong> comes directly from the Ruach field. The rest — <strong style={{ color: '#e060ff' }}>{result.qcdPct.toFixed(0)}%</strong> — is the energy of the hidden bond, the gluon confinement that holds your quarks together. "In him all things hold together." — Colossians 1:17. You are not primarily Ruach-weight. You are primarily the energy of binding. The Ruach gave form to your quarks. The bond gives you your weight.</>
                : <>Of your <strong style={{ color: '#fff' }}>{result.weightKg.toFixed(1)} kg</strong>, only <strong style={{ color: accentColor }}>~{(result.higgsMassKg * 1000).toFixed(3)} grams</strong> comes from the Higgs field. The other <strong style={{ color: '#e060ff' }}>{result.qcdPct.toFixed(0)}%</strong> — {result.qcdMassKg.toFixed(2)} kg — is pure QCD binding energy: E=mc² running in reverse, energy becoming mass through confinement. You are not made of Higgs mass. You are made of confined energy.</>
              }
            </p>
            {isRuach && (
              <p style={{ fontSize: '0.78rem', color: `${accentColor}70`, fontStyle: 'italic', margin: '0.75rem 0 0', lineHeight: 1.6 }}>
                "I praise you because I am fearfully and wonderfully made; your works are wonderful, I know that full well." — Psalm 139:14
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
